// src/components/goals/GoalPointApprovals.js
//
// Admin Reports → "Goal points to approve". The admin's rules (Edit goal on the
// Coordinator page) turn coordinators' weekly reports into goal points; each
// card here is one coordinator x goal x month with points not yet decided:
// the rules met, the weeks credited and the dates. Approve adds them to that
// coordinator's monthly and weekly counts (after a before -> after
// confirmation); Dismiss leaves the counts alone. Nothing moves without this.
// Logic: services/goalRules.js; reads and writes: services/goalPointService.js.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Target, CheckCircle2, XCircle, AlertTriangle, ArrowRight, Loader2, RefreshCw } from "lucide-react";
import { auth } from "../../firebase";
import { buildGoalPointCards, GOAL_RULE_METRICS } from "../../services/goalRules";
import { loadGoalPointData, previewDecision, decideGoalPoints } from "../../services/goalPointService";
import { monthLabel, periodLabel, PERIOD_TYPES } from "../../services/enrollmentPeriods";
import { WrModal, BTN_PRIMARY, BTN_SOFT, BTN_SMALL } from "../weeklyReport/ui";

const weekText = (w) => periodLabel(PERIOD_TYPES.WEEK, w) || w;
const goalLabel = (metric) => GOAL_RULE_METRICS[metric]?.label || metric;

const BeforeAfter = ({ label, before, after }) => (
  <div className="flex items-center justify-between gap-3 py-1.5" role="group" aria-label={`${label}: ${before} to ${after}`}>
    <span className="text-sm text-slate-300">{label}</span>
    <span className="text-sm font-semibold text-white tabular-nums inline-flex items-center gap-1.5">
      {before}
      <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
      <span className={after !== before ? "goal-points-changed text-emerald-300" : ""}>{after}</span>
    </span>
  </div>
);

// scrollOnLoad: bring this section into view once it has loaded (the home
// page's link here, UI revamp). Off = as before.
const GoalPointApprovals = ({ scrollOnLoad = false } = {}) => {
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [confirm, setConfirm] = useState(null); // { card, status, preview, loading, error }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await loadGoalPointData();
      setState({ loading: false, error: "", data });
    } catch (err) {
      console.error("❌ Could not load goal points:", err);
      setState({ loading: false, error: "Goal points couldn't be loaded.", data: null });
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const loaded = !state.loading && !state.error;
  useEffect(() => {
    if (scrollOnLoad && loaded) document.getElementById("goal-points")?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [scrollOnLoad, loaded]);

  const cards = useMemo(() => (state.data ? buildGoalPointCards(state.data) : []), [state.data]);
  const hasRules = useMemo(
    () => !!state.data && Object.values(state.data.ruleSetsByMetric).some((sets) => sets.some((s) => s.rules.length)),
    [state.data]
  );

  const open = async (card, status) => {
    setConfirm({ card, status, preview: null, loading: status === "approved", error: "" });
    if (status !== "approved") return;
    try {
      const preview = await previewDecision({ card, roster: state.data?.roster });
      setConfirm((c) => (c && c.card.id === card.id ? { ...c, preview, loading: false } : c));
    } catch (err) {
      console.error("❌ Could not preview:", err);
      setConfirm((c) => (c ? { ...c, loading: false, error: "Couldn't load the current counts. Try again." } : c));
    }
  };

  const decide = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await decideGoalPoints({ card: confirm.card, status: confirm.status, by: auth.currentUser?.email || "" });
      setConfirm(null);
      await load();
    } catch (err) {
      console.error("❌ Could not save the decision:", err);
      setConfirm((c) => (c ? { ...c, error: err?.message || "Couldn't save. Please try again." } : c));
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) return <p className="text-sm text-slate-400 py-2">Checking goal points…</p>;
  if (state.error) return <p className="text-sm text-rose-300 py-2">{state.error}</p>;

  return (
    <section id="goal-points" className="goal-points-panel rounded-2xl border border-emerald-400/30 bg-emerald-500/[0.07] p-4" aria-label="Goal points to approve">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="goal-points-icon w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
          <Target className="w-4 h-4 text-emerald-300" />
        </div>
        <h3 className="text-sm font-semibold text-white flex-1">
          Goal points to approve{cards.length ? ` (${cards.length})` : ""}
        </h3>
        <button type="button" className={BTN_SMALL} onClick={load} aria-label="Refresh goal points">
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {!hasRules ? (
        <p className="text-sm text-slate-300">
          No goal rules yet. Set them with Edit goal on the Coordinator page; points from weekly reports then show up
          here for approval.
        </p>
      ) : cards.length === 0 ? (
        <p className="text-sm text-slate-300">Nothing new. Every point earned so far has been decided.</p>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5" aria-label="Goal point cards">
          {cards.map((c) => (
            <li key={c.id} className="goal-points-card flex flex-col rounded-xl border border-white/10 bg-white/[0.04] p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{c.coordinator}</div>
                  <div className="text-[11px] text-slate-300">
                    {goalLabel(c.metric)} · {monthLabel(c.monthId) || c.monthId}
                  </div>
                </div>
                {c.newPoints > 0 && (
                  <span className="goal-points-badge shrink-0 rounded-lg bg-emerald-500/20 border border-emerald-400/40 px-2 py-0.5 text-sm font-bold text-emerald-200">
                    +{c.newPoints}
                  </span>
                )}
              </div>
              <div className="mt-1 text-[11px] text-slate-400">
                Reports {c.periodStart || "?"} → {c.periodEnd || "?"} · rules from {monthLabel(c.rulesFrom) || c.rulesFrom}
              </div>
              <ul className="mt-2 space-y-0.5">
                {c.breakdown
                  .filter((b) => b.points > 0)
                  .map((b) => (
                    <li key={b.ruleId} className="text-[11px] text-slate-200">
                      <span className="font-semibold">{b.points} pt</span> · {b.text}
                    </li>
                  ))}
              </ul>
              {c.newPoints > 0 && (
                <div className="mt-1 text-[11px] text-slate-400">
                  Weeks: {Object.entries(c.newWeekly).map(([w, p]) => `${weekText(w)} +${p}`).join(", ")}
                </div>
              )}
              {!c.onRoster && (
                <p className="goal-points-warn mt-2 flex items-start gap-1 text-[11px] text-amber-300">
                  <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                  {c.coordinator} isn't on the leaderboard, so these points won't show in the circle.
                </p>
              )}
              {c.behindBy > 0 && (
                <p className="goal-points-warn mt-2 flex items-start gap-1 text-[11px] text-amber-300">
                  <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                  {c.behindBy} point{c.behindBy === 1 ? "" : "s"} fewer than already decided (rules or reports changed).
                  Nothing was taken away; fix the count by hand if needed.
                </p>
              )}
              {c.newPoints > 0 && (
                <div className="mt-auto pt-3 flex flex-wrap justify-end gap-2">
                  <button type="button" className={BTN_SMALL} onClick={() => open(c, "dismissed")}>
                    <XCircle className="w-3 h-3" />
                    Dismiss
                  </button>
                  <button type="button" className={`${BTN_PRIMARY} !px-3 !py-1.5 !text-xs`} onClick={() => open(c, "approved")}>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Approve
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <WrModal
        open={!!confirm}
        onClose={() => !busy && setConfirm(null)}
        icon={confirm?.status === "dismissed" ? XCircle : CheckCircle2}
        title={confirm?.status === "dismissed" ? "Dismiss these points?" : "Add these goal points?"}
        subtitle={
          confirm
            ? `${confirm.card.coordinator} · ${goalLabel(confirm.card.metric)} · ${monthLabel(confirm.card.monthId)} · +${confirm.card.newPoints}`
            : ""
        }
        footer={
          <>
            <button type="button" className={`${BTN_SOFT} mr-auto`} onClick={() => setConfirm(null)} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className={BTN_PRIMARY}
              onClick={decide}
              disabled={busy || confirm?.loading || (confirm?.status === "approved" && !confirm?.preview)}
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {confirm?.status === "dismissed" ? "Dismiss" : "Approve and add"}
            </button>
          </>
        }
      >
        {confirm?.status === "dismissed" ? (
          <p className="text-sm text-slate-300">
            The counts stay as they are, and these {confirm.card.newPoints} point(s) won't be offered again. Points
            earned later still show up.
          </p>
        ) : confirm?.loading ? (
          <p className="text-sm text-slate-400">Loading the current counts…</p>
        ) : confirm?.preview ? (
          <div>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-1">
              <BeforeAfter
                label={`${confirm.card.coordinator}, ${monthLabel(confirm.card.monthId)}`}
                before={confirm.preview.monthBefore}
                after={confirm.preview.monthAfter}
              />
              {confirm.preview.weeks.map((w) => (
                <BeforeAfter key={w.weekOf} label={weekText(w.weekOf)} before={w.before} after={w.after} />
              ))}
              <BeforeAfter label="Goal circle total" before={confirm.preview.circleBefore} after={confirm.preview.circleAfter} />
            </div>
            {confirm.preview.monthClosed && (
              <p className="goal-points-warn mt-2 text-[11px] text-amber-300">
                {monthLabel(confirm.card.monthId)} is already closed. Re-close it to update its snapshot.
              </p>
            )}
            <p className="mt-2 text-[11px] text-slate-400">Added on top of the current counts. Each point is added once.</p>
          </div>
        ) : null}
        {confirm?.error && (
          <p className="weekly-report-error mt-3 rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200">
            {confirm.error}
          </p>
        )}
      </WrModal>
    </section>
  );
};

export default GoalPointApprovals;
